import React from 'react';
import { App, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';

/**
 * Un botón que se VE deshabilitado pero SÍ responde al click, para explicar
 * por qué no está disponible.
 *
 * POR QUÉ NO USA `disabled` DE VERDAD. antd le pone `pointer-events: none` a
 * los botones deshabilitados, así que no disparan `onClick`. Un botón gris que
 * no hace nada deja al usuario con la pregunta ("¿por qué no puedo?") y sin la
 * respuesta. Acá se replica el aspecto —gris, cursor de bloqueado— pero el
 * click se escucha y abre la explicación.
 *
 * POR QUÉ NO SE OCULTA EL BOTÓN. Se evaluó y se descartó: mostrarlo bloqueado
 * le dice al productor que la función existe, y ése es el momento exacto en que
 * le interesa saber cómo conseguirla — mucho más certero que un cartel genérico
 * en el dashboard.
 *
 * LOS DOS MOTIVOS SON DISTINTOS Y NO SE MEZCLAN:
 *
 *   `plan`   — tu membresía no incluye esto. Se arregla contratando.
 *   `ajeno`  — el recurso es de OTRA cabaña. No se arregla con plata, y
 *              ofrecerle un upgrade sería mentirle: por más que contrate
 *              Enterprise, el remate del vecino sigue sin ser suyo.
 *
 * Meter los dos en un solo mensaje era la salida fácil y la equivocada.
 */

const MOTIVOS = {
  plan: {
    titulo: 'Función de otro plan',
    cuerpo:
      'Tu membresía actual no incluye esta herramienta. Escribinos y vemos ' +
      'juntos qué plan te sirve para lo que necesitás hacer.',
  },
  ajeno: {
    titulo: 'Este remate no es tuyo',
    cuerpo:
      'Te invitaron a consignar animales en el remate de otra cabaña. Podés ' +
      'ver todo y seguir tus animales, pero organizarlo —editarlo, finalizarlo ' +
      'o eliminarlo— le corresponde a la cabaña que lo creó.',
  },
  // `ajeno` habla de REMATES, y se estaba reusando para cabañas: el productor
  // apretaba editar una cabaña consignataria y leia "este remate no es tuyo",
  // que no tiene nada que ver. Dos cosas distintas necesitan dos mensajes.
  cabana_ajena: {
    titulo: 'Esta cabaña es de otra empresa',
    cuerpo:
      'La ves porque te consigna animales en tu remate, y necesitás su nombre ' +
      'para el catálogo. Pero sus datos los administra ella: cambiarlos sería ' +
      'modificarle el nombre con el que aparece en su propio micrositio.',
  },
};

const BotonBloqueado = ({
  motivo = 'plan',
  detalle,
  icon,
  children,
  // Se descarta a proposito: si el llamador pasa `disabled`, antd le pone
  // pointer-events:none y mata el click que este componente existe para
  // conservar. Quedaria un boton gris sin explicacion, o sea lo que veniamos
  // a evitar.
  disabled: _ignorado,
  ...props
}) => {
  // App.useApp() y no el Modal estatico: el estatico no consume el context de
  // <App>, asi que se queda sin el theme y sin la config de la app. El resto
  // del repo ya usa el hook en seis archivos.
  const { modal } = App.useApp();
  // Una sola resolucion del motivo, usada por el texto Y por el aria-label:
  // con dos fallbacks separados, un motivo desconocido daba el cuerpo de `plan`
  // y un aria-label vacio.
  const explicacion = MOTIVOS[motivo] ?? MOTIVOS.plan;

  const explicar = () => {
    const { titulo, cuerpo } = explicacion;
    modal.info({
      title: titulo,
      content: detalle || cuerpo,
      okText: 'Entendido',
      icon: <LockOutlined style={{ color: '#faad14' }} />,
    });
  };

  return (
    <Button
      {...props}
      icon={icon}
      onClick={explicar}
      // Se imita el aspecto de `disabled` sin el atributo, que mataría el click.
      style={{
        color: 'rgba(0, 0, 0, 0.25)',
        borderColor: props.type === 'text' ? 'transparent' : '#d9d9d9',
        cursor: 'not-allowed',
        ...props.style,
      }}
      // Que el lector de pantalla no anuncie "no disponible" y se coma la
      // explicación: el botón SÍ hace algo.
      aria-label={props['aria-label'] || explicacion.titulo}
    >
      {children}
    </Button>
  );
};

export default BotonBloqueado;
